import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

/*  tweddi — Reset Password (/reset-password)
    tweddi identity: cream ground, Inclusive Sans, near-mono ink, apricot only
    as a rare tiny accent (the ✳), the woven monogram wordmark, hairline
    inputs with eye-toggles, circle-arrow submit. Token validation + all
    states (invalid / done / form) and testids preserved.                    */

const API   = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CREAM = '#FFFDF8';
const WASH  = '#FBF6EF';
const INK   = '#141313';
const MUTE  = '#6E6B62';
const APRICOT = '#F4A26B';
const RULE  = 'rgba(20,19,19,0.10)';

const Mark = ({ size = 26, mark = INK, bg = CREAM }) => (
  <svg viewBox="-60 -60 664 520" width={size} height={size * (520/664)} style={{ overflow: 'visible', display: 'block', flexShrink: 0 }} aria-label="tweddi">
    <circle cx="200" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={bg}   strokeWidth="108" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={bg}   strokeWidth="108" />
    <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={mark} strokeWidth="66" />
  </svg>
);

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => { if (!token) setInvalid(true); }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setIsLoading(true);
    try { await axios.post(`${API}/auth/reset-password`, { token, new_password: password }); setDone(true); }
    catch (err) { const msg = err.response?.data?.detail || 'Reset failed'; if (msg.includes('expired') || msg.includes('invalid')) setInvalid(true); else toast.error(msg); }
    finally { setIsLoading(false); }
  };

  const inputStyle = { width: '100%', border: 'none', borderBottom: `1.5px solid ${RULE}`, background: 'transparent', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.95rem', fontWeight: 600, color: INK, padding: '0.65rem 0', outline: 'none', letterSpacing: '0.01em', transition: 'border-color 0.2s', caretColor: INK };

  const eyebrow = invalid ? 'Link issue' : done ? 'Done' : 'Reset password';
  const headline = invalid ? <>Link expired.</> : done ? <>Password updated.</> : <>Set a new<br />password.</>;

  return (
    <div style={{ background: `linear-gradient(180deg, ${CREAM} 55%, ${WASH} 100%)`, minHeight: '100dvh', maxWidth: '30rem', margin: '0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '2rem 1.75rem' }} data-testid="reset-password-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');
        * { box-sizing: border-box; }
        .bb-input:focus { border-bottom-color: ${INK} !important; outline: none; }
        .bb-input::placeholder { color: rgba(20,19,19,0.35); font-weight: 500; }
        .rp-circle { transition: transform 0.25s cubic-bezier(.16,1,.3,1); }
        .rp-cta:hover .rp-circle { transform: translateX(3px); }
        .rp-tw { display: inline-block; animation: rp-tw 2.6s ease-in-out infinite; transform-origin: center; }
        @keyframes rp-tw { 0%,100%{transform:scale(1) rotate(0);opacity:.7} 50%{transform:scale(1.3) rotate(90deg);opacity:1} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .rp-tw { animation: none; } }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '3rem' }}>
        <Mark size={26} mark={INK} bg={CREAM} />
        <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.15rem', fontWeight: 600, color: INK, letterSpacing: '-0.01em' }}>tweddi</span>
      </div>

      <div style={{ borderTop: `1px solid ${INK}`, paddingTop: '1.5rem', marginBottom: '2.5rem' }}>
        <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTE, marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          {eyebrow} {done && <span className="rp-tw" style={{ fontSize: '0.7rem', color: APRICOT }}>✳</span>}
        </p>
        <h1 style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: 'clamp(2.3rem, 9vw, 3rem)', fontWeight: 600, color: INK, lineHeight: 1.03, letterSpacing: '-0.03em' }}>
          {headline}
        </h1>
      </div>

      {invalid && (
        <div>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE, lineHeight: 1.7, marginBottom: '1.5rem' }}>This reset link is no longer valid. Request a new one from the sign in page.</p>
          <button onClick={() => navigate('/auth')} style={{ background: 'none', border: 'none', borderBottom: `1.5px solid ${INK}`, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.64rem', fontWeight: 600, letterSpacing: '0.08em', color: INK, padding: '0 0 2px' }}>Back to sign in →</button>
        </div>
      )}

      {done && (
        <div>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.1rem', fontStyle: 'italic', color: INK, marginBottom: '0.75rem' }}>Your password has been updated.</p>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE, lineHeight: 1.7, marginBottom: '1.5rem' }}>Sign in with your new credentials.</p>
          <button onClick={() => navigate('/auth')} style={{ background: 'none', border: 'none', borderBottom: `1.5px solid ${INK}`, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.64rem', fontWeight: 600, letterSpacing: '0.08em', color: INK, padding: '0 0 2px' }}>Sign in →</button>
        </div>
      )}

      {!invalid && !done && (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
          {[{ label: 'New password', show: showPwd, toggle: () => setShowPwd(v => !v), val: password, set: setPassword, testid: 'new-password' }, { label: 'Confirm password', show: showConfirm, toggle: () => setShowConfirm(v => !v), val: confirm, set: setConfirm, testid: 'confirm-password' }].map((f, i) => (
            <div key={i}>
              <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTE, marginBottom: '0.6rem' }}>{f.label}</p>
              <div style={{ position: 'relative' }}>
                <input className="bb-input" type={f.show ? 'text' : 'password'} placeholder={i === 0 ? 'At least 6 characters' : 'Repeat your password'} value={f.val} onChange={(e) => f.set(e.target.value)} required minLength={i === 0 ? 6 : undefined} style={{ ...inputStyle, paddingRight: '2rem' }} data-testid={f.testid} />
                <button type="button" onClick={f.toggle} tabIndex={-1} style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTE, display: 'flex', alignItems: 'center' }}>
                  {f.show ? <EyeOff size={15} strokeWidth={1.75} /> : <Eye size={15} strokeWidth={1.75} />}
                </button>
              </div>
            </div>
          ))}
          <button type="submit" disabled={isLoading} data-testid="reset-submit" className="rp-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK, opacity: isLoading ? 0.5 : 1, marginTop: '0.25rem' }}>
            {isLoading ? 'Updating…' : 'Update password'}
            <span className="rp-circle" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2.2rem', height: '2.2rem', borderRadius: '50%', background: INK }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={CREAM} strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
          </button>
        </form>
      )}
    </div>
  );
}
